<template>
  <div class="flex flex-col items-center text-center gap-4 py-8 px-2" dir="rtl">
    <!-- Waiting for the customer at the terminal -->
    <template v-if="state === 'waiting'">
      <div class="pulse-ring">
        <i class="pi pi-credit-card text-3xl text-primary" aria-hidden="true" />
      </div>
      <div class="flex flex-col gap-1.5">
        <span class="text-lg font-bold text-surface-900">החיוב נשלח למסוף</span>
        <span class="text-sm text-surface-600 leading-relaxed">
          בקשו מהלקוח להעביר או להצמיד את הכרטיס במסוף{{ terminalName ? ` ״${terminalName}״` : '' }}
        </span>
        <span v-if="amount" class="text-2xl font-extrabold text-surface-900 mt-1">{{ ils(amount) }}</span>
      </div>
      <span class="text-xs text-surface-500 flex items-center gap-1.5" role="status">
        <i class="pi pi-spin pi-spinner !text-xs" aria-hidden="true" />
        {{ slow ? 'עדיין ממתינים לתשובה מהמסוף…' : 'ממתינים לתשובה מהמסוף' }}
      </span>
      <template v-if="slow">
        <div class="flex gap-2 w-full mt-2">
          <button type="button" class="act act-secondary flex-1" :disabled="retrying" @click="retry">שליחה מחדש למסוף</button>
          <button type="button" class="act act-secondary flex-1" @click="emit('back')">חזרה</button>
        </div>
        <!-- The sale stays open on the terminal until it's paid or cancelled there -->
        <span class="text-xs text-amber-700 leading-relaxed">לפני שחוזרים או גובים בדרך אחרת, בטלו את החיוב במסוף — אחרת הלקוח עלול לשלם פעמיים</span>
      </template>
    </template>

    <!-- Paid -->
    <template v-else-if="state === 'success'">
      <div class="result-icon bg-green-50 text-green-600">
        <i class="pi pi-check text-3xl" aria-hidden="true" />
      </div>
      <div class="flex flex-col gap-1">
        <span class="text-lg font-bold text-surface-900">התשלום התקבל</span>
        <span class="text-sm text-surface-600">הקבלה תישלח ללקוח</span>
      </div>
    </template>

    <!-- Declined / failed -->
    <template v-else>
      <div class="result-icon bg-red-50 text-red-600">
        <i class="pi pi-times text-3xl" aria-hidden="true" />
      </div>
      <div class="flex flex-col gap-1">
        <span class="text-lg font-bold text-surface-900">החיוב לא עבר</span>
        <span class="text-sm text-surface-600 leading-relaxed">{{ failureReason || 'המסוף דחה את החיוב' }}</span>
      </div>
      <div class="flex gap-2 w-full mt-2">
        <button type="button" class="act act-primary flex-1" :disabled="retrying" @click="retry">
          <i v-if="retrying" class="pi pi-spin pi-spinner !text-sm" aria-hidden="true" />
          ניסיון חוזר במסוף
        </button>
        <button type="button" class="act act-secondary flex-1" @click="emit('back')">אמצעי תשלום אחר</button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useToast } from 'vue-toastification';
import { PaymentRequestStatus } from '@torim-app/types';
import { PaymentsApi } from '@app/api/admin';
import { trackEvent } from '@app/composables/posthog';

/**
 * Shown after a payment request was sent to a PayMe card terminal: polls the request until
 * PayMe's callback marks it paid or failed. The webhook does all the bookkeeping (invoice,
 * receipt message, appointment status) — this only reports the outcome.
 */
const props = defineProps<{
  paymentUuid: string;
  posId: string;
  terminalName?: string;
  amount?: number;
}>();

const emit = defineEmits<{ completed: []; back: [] }>();

const toast = useToast();
const state = ref<'waiting' | 'success' | 'failed'>('waiting');
const failureReason = ref<string>();
const slow = ref(false);
const retrying = ref(false);

const POLL_MS = 2000;
// Most taps land within a minute; after that offer to resend instead of waiting forever.
const SLOW_AFTER_MS = 90_000;

let pollTimer: ReturnType<typeof setTimeout> | undefined;
let slowTimer: ReturnType<typeof setTimeout> | undefined;
let stopped = false;

const ils = (n: number) => `₪${n.toLocaleString('he-IL', { maximumFractionDigits: 2 })}`;

const poll = async () => {
  if (stopped) return;
  try {
    const { status, failureReason: reason } = await PaymentsApi.getPosChargeStatus(props.paymentUuid);
    if (status === PaymentRequestStatus.Success) {
      state.value = 'success';
      trackEvent('pos_charge_succeeded');
      stop();
      setTimeout(() => emit('completed'), 1400);
      return;
    }
    if (status === PaymentRequestStatus.Failed) {
      state.value = 'failed';
      failureReason.value = reason;
      trackEvent('pos_charge_failed', { reason });
      stop();
      return;
    }
  } catch (e) {
    // A dropped poll is fine — the next one catches up.
    console.error(e);
  }
  pollTimer = setTimeout(poll, POLL_MS);
};

const start = () => {
  stopped = false;
  slow.value = false;
  state.value = 'waiting';
  clearTimeout(slowTimer);
  slowTimer = setTimeout(() => { slow.value = true; }, SLOW_AFTER_MS);
  pollTimer = setTimeout(poll, POLL_MS);
};

const stop = () => {
  stopped = true;
  clearTimeout(pollTimer);
  clearTimeout(slowTimer);
};

const retry = async () => {
  if (retrying.value) return;
  retrying.value = true;
  stop();
  try {
    const result = await PaymentsApi.startPosCharge({ paymentRequestUuid: props.paymentUuid, posId: props.posId });
    if (!result.success) {
      // The earlier sale is still open (or was just paid) on the terminal — keep waiting on it.
      if (result.pending) {
        toast.info(result.error || 'החיוב הקודם עדיין פתוח במסוף');
        start();
        return;
      }
      state.value = 'failed';
      failureReason.value = result.error;
      return;
    }
    trackEvent('pos_charge_retried');
    start();
  } catch (e: any) {
    if (e?.message === 'already-paid') {
      state.value = 'success';
      setTimeout(() => emit('completed'), 1400);
      return;
    }
    toast.error('לא הצלחנו לשלוח את החיוב למסוף');
    state.value = 'failed';
  } finally {
    retrying.value = false;
  }
};

onMounted(start);
onBeforeUnmount(stop);
</script>

<style scoped lang="scss">
.pulse-ring {
  @apply w-20 h-20 rounded-full bg-primary-50 flex items-center justify-center relative;

  &::after {
    content: '';
    @apply absolute inset-0 rounded-full border-2 border-primary-300;
    animation: pulse 1.6s ease-out infinite;
  }
}

@keyframes pulse {
  from { transform: scale(1); opacity: 0.8; }
  to { transform: scale(1.45); opacity: 0; }
}

.result-icon {
  @apply w-20 h-20 rounded-full flex items-center justify-center;
}

.act {
  @apply h-12 rounded-[14px] text-[15px] font-semibold flex items-center justify-center gap-2;

  &:disabled {
    @apply opacity-60;
  }
}

.act-primary {
  @apply bg-primary text-white;
}

.act-secondary {
  @apply bg-surface-100 text-surface-800;
}
</style>
